/* Sales Sample Tool — Sales Rep variant of the Sample Request Tool
   (sample-request.js), same single-page 4-step flow.
   Step 1 Qualify (+ Rep email, packing type) -> Step 2 Pick products ->
   Step 3 Review & ship -> Step 4 Confirmation.
   Differences from the customer tool: no sample fee/payment, no hard piece
   cap — kits above the standard limit (APPROVAL_THRESHOLD) need an approval
   confirmation popup before shipping — and the payload carries repName,
   repEmail, packingType, overLimitApproved, salesManagerEmail and
   salesRepOrder: true.
   Draft state lives in localStorage (sst_qualify, sst_selected, sst_shipping,
   sst_order, sst_approved); the catalog/filter caches are shared with the
   customer tool. Submission posts to the backend email service, falling back
   to Shopify's native contact form. */

(function () {
  'use strict';

  var root = document.getElementById('sales-sample-tool');
  if (!root) return;

  // Standard sample limit — kits above it need over-limit approval.
  var APPROVAL_THRESHOLD = parseInt(root.dataset.maxPieces, 10) || 15;
  var COLLECTION = root.dataset.collection || 'all';
  var CONTACT_EMAIL = root.dataset.contactEmail || 'service@restaurantware.com';
  // Added to the internal email by the backend when the kit is over the limit.
  var SALES_MANAGER_EMAIL = root.dataset.salesManagerEmail || '';
  /* Backend email service the request is POSTed to. Set to '' to fall back
     to Shopify's native contact form email. */
  var EMAIL_ENDPOINT = 'https://api.rwdomain.com/api/v1/main-us/sample-kit/request';
  /* Google Maps key for address autocomplete on the shipping step. Set to ''
     to disable. Restrict this key to your domains in Google Cloud Console. */
  var MAPS_API_KEY = 'AIzaSyDI3RRB1e1bVKrYfAXtNAo65u29BPGIRls';

  /* Boost AI Search & Filter is already installed on this store (see the
     app-embed blocks in config/settings_data.json). Its public storefront
     search endpoint is unauthenticated and scoped by shop domain — the same
     way Boost's own Instant Search widget calls it from the browser. Reusing
     it here means the picker gets Boost's fuzzy/typo-tolerant/synonym
     matching instead of maintaining our own SYNONYMS list by hand.
     NOT VERIFIED against this store's live Boost account/plan — if the
     response shape differs, boostSearchSkus() fails closed (returns null)
     and matchProduct() falls back to the local matcher below, so search
     never breaks even if this integration needs adjusting. */
  var BOOST_SHOP_DOMAIN = root.dataset.shopDomain || '';
  var BOOST_SEARCH_ENDPOINT = 'https://services.mybcapps.com/bc-sf-filter/search/suggest';
  var boostCache = {}; // query -> Promise<Set-like sku map|null>, avoids refetching on back/forward edits
  var boostQuery = ''; // the query the current boostSkuMap actually answers
  var boostSkuMap = null;
  var boostPending = false; // true while Boost is still resolving the current query

  // A single page tops out well short of total_product for common terms, so
  // one page alone would silently drop most real matches. Cap how many extra
  // pages we chase so a broad one-letter query can't fire off hundreds of
  // requests. total_page in Boost's response is NOT reliable for this — it
  // came back as the same value (37) for two queries with very different
  // total_product (59 vs 579) — so pagination below is driven off
  // total_product and the page size actually observed, not total_page.
  var BOOST_MAX_PAGES = 20;

  function boostFetchPage(query, page) {
    var url = BOOST_SEARCH_ENDPOINT + '?shop=' + encodeURIComponent(BOOST_SHOP_DOMAIN) +
      '&q=' + encodeURIComponent(query) + '&autocorrect=1&page=' + page + '&limit=250';
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function boostCollectSkus(data, skus) {
    var products = (data && data.products) || (data && data.suggestions && data.suggestions.products) || [];
    products.forEach(function (p) {
      if (p.sku) skus[String(p.sku).toUpperCase()] = true;
      (p.skus || []).forEach(function (s) { if (s) skus[String(s).toUpperCase()] = true; });
      (p.variants || []).forEach(function (v) { if (v.sku) skus[String(v.sku).toUpperCase()] = true; });
    });
    return products.length;
  }

  function boostSearchSkus(query) {
    if (!BOOST_SHOP_DOMAIN || !query) return Promise.resolve(null);
    if (boostCache[query]) return boostCache[query];
    var promise = boostFetchPage(query, 1)
      .then(function (first) {
        var skus = {};
        var pageSize = boostCollectSkus(first, skus);
        var totalProduct = (first && first.total_product) || pageSize;
        if (!pageSize || pageSize >= totalProduct) return skus;
        var totalPages = Math.min(Math.ceil(totalProduct / pageSize), BOOST_MAX_PAGES);
        if (totalPages <= 1) return skus;
        var rest = [];
        for (var p = 2; p <= totalPages; p++) rest.push(boostFetchPage(query, p));
        return Promise.all(rest).then(function (pages) {
          pages.forEach(function (data) { boostCollectSkus(data, skus); });
          return skus;
        });
      })
      .catch(function () { return null; }); // Boost unreachable/unexpected shape — caller falls back to local matching
    boostCache[query] = promise;
    return promise;
  }

  /* Inline section data — the disposable-menu tag list and the search
     synonym groups, both editable from the section settings without a code
     deploy. The category / collection dropdown entries are fetched from the
     srt-filter-data section and cached in localStorage (see loadFilterOptions). */
  var filterData = { disposableCats: '', searchSynonyms: '' };
  try {
    var dataEl = document.getElementById('srt-filter-data');
    if (dataEl) filterData = JSON.parse(dataEl.textContent) || filterData;
  } catch (e) { /* scope stays open */ }

  /* Disposable-menu scope — comma/newline separated cat-* tags pasted in the
     section settings. Products outside the scope are still shown (so brand
     filters like Adapt Tek aren't empty) but grayed as "Not eligible for
     samples"; only in-scope products can be added to the kit. */
  var DISPOSABLE_CATS = String(filterData.disposableCats || '')
    .split(/[\s,]+/)
    .map(function (t) { return t.trim().toLowerCase(); })
    .filter(Boolean);

  /* Eligibility rules from the Sample Tool dev guide (Part 2 / Part 9).
     A reusable/janitorial title word makes the product ineligible UNLESS a
     disposable material word is also present — "Plastic Glass" is a plastic
     disposable, not real glass, so it stays eligible. */
  var REUSABLE_TITLE = /\b(polycarbonate|porcelain|ceramic|stoneware|china|melamine|crystal|stainless steel|mason jar|wine glass|shot glass|cocktail glass|coffee mug|copper mug|glass mug|glass jar|glass pitcher|glass decanter|glass bowl|glass plate|frying pan|stock pot|chef knife|cutting board|whisk|ladle|serving tongs|chafing dish|reusable|washable)\b/i;
  var JANITORIAL_TITLE = /\b(mop|broom|dustpan|urinal|toilet paper|sanitizer|disinfectant|work gloves?|floor mat|floor sign|first aid|scrubber|sponge|microfiber|paper towel|hand towel|trash bag|garbage bag|trash can|urinal cake)\b/i;
  var DISPOSABLE_ADJ = /\b(disposable|plastic|paper|foil|bagasse|sugarcane|palm leaf|compostable|kraft|pla|pet|pulp)\b/i;

  /* ---------- storage helpers ---------- */
  function sGet(key, fallback) {
    try {
      var v = JSON.parse(localStorage.getItem(key));
      return v === null || v === undefined ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }
  function sSet(key, val) { localStorage.setItem(key, JSON.stringify(val)); }
  function sDel(key) { localStorage.removeItem(key); }

  /* Draft state survives a plain refresh (restored below from sst_qualify /
     sst_selected / sst_shipping). It is only cleared by "Start over" or by
     the contact-form redirect (contact_posted=true) once the request has
     actually been submitted. */
  var posted = !!document.getElementById('srt-posted') || /contact_posted=true/.test(window.location.search);

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- inline field errors ----------
     Validation messages render in red directly below the offending field
     instead of a blocking alert(). Editing the field clears its message. */
  function setFieldError(form, name, msg) {
    var el = form.elements[name];
    if (!el) return;
    el.classList.add('srt-input--error');
    var next = el.nextElementSibling;
    var err = next && next.classList && next.classList.contains('srt-field-error') ? next : null;
    if (!err) {
      err = document.createElement('div');
      err.className = 'srt-field-error';
      el.insertAdjacentElement('afterend', err);
    }
    err.textContent = msg;
  }
  /* Radio groups: form.elements[name] is a RadioNodeList, which has no single
     element to mark, so the message goes after the wrapping fieldset. */
  function setGroupError(groupEl, msg) {
    if (!groupEl) return;
    groupEl.classList.add('sst-radio-group--error');
    var next = groupEl.nextElementSibling;
    var err = next && next.classList && next.classList.contains('srt-field-error') ? next : null;
    if (!err) {
      err = document.createElement('div');
      err.className = 'srt-field-error';
      groupEl.insertAdjacentElement('afterend', err);
    }
    err.textContent = msg;
  }
  function clearGroupError(groupEl) {
    if (!groupEl || !groupEl.classList.contains('sst-radio-group--error')) return;
    groupEl.classList.remove('sst-radio-group--error');
    var next = groupEl.nextElementSibling;
    if (next && next.classList.contains('srt-field-error')) next.parentNode.removeChild(next);
  }
  function clearFieldErrors(form) {
    Array.prototype.forEach.call(form.querySelectorAll('.srt-field-error'), function (n) {
      n.parentNode.removeChild(n);
    });
    Array.prototype.forEach.call(form.querySelectorAll('.srt-input--error'), function (n) {
      n.classList.remove('srt-input--error');
    });
    Array.prototype.forEach.call(form.querySelectorAll('.sst-radio-group--error'), function (n) {
      n.classList.remove('sst-radio-group--error');
    });
  }
  function focusFirstError(form) {
    var el = form.querySelector('.srt-input--error') || form.querySelector('.sst-radio-group--error input');
    if (el) el.focus();
  }
  /* Also called directly when a field is filled programmatically (Places
     autocomplete, ZIP lookup) — those writes fire no input/change events. */
  function clearFieldError(el) {
    if (!el || !el.classList || !el.classList.contains('srt-input--error')) return;
    el.classList.remove('srt-input--error');
    var next = el.nextElementSibling;
    if (next && next.classList.contains('srt-field-error')) next.parentNode.removeChild(next);
  }
  function clearErrorOnEdit(e) {
    clearFieldError(e.target);
    if (e.target && e.target.type === 'radio') clearGroupError(e.target.closest('.sst-radio-group'));
  }
  root.addEventListener('input', clearErrorOnEdit);
  root.addEventListener('change', clearErrorOnEdit);

  /* ---------- step navigation ---------- */
  var steps = root.querySelectorAll('.srt-step');

  function goToStep(n) {
    steps.forEach(function (s) {
      s.classList.toggle('srt-step--active', s.dataset.step === String(n));
    });
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (n === 2 && !productsRequested) loadProducts();
    if (n === 2) renderGrid();
    if (n === 3) enterShipping();
    if (n === 4) renderConfirmation();
  }

  /* ================================================================
     STEP 1 — QUALIFY
     ================================================================ */
  var CONSUMER = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'me.com', 'msn.com', 'live.com', 'proton.me', 'protonmail.com', 'ymail.com', 'mail.com', 'gmx.com'];
  var DISPOSABLE = ['mailinator.com', 'tempmail.com', '10minutemail.com', 'guerrillamail.com', 'yopmail.com', 'trashmail.com', 'throwaway.email', 'fakeinbox.com', 'sharklasers.com', 'maildrop.cc', 'getnada.com', 'dispostable.com', 'tempinbox.com', 'mintemail.com'];

  var qualifyForm = root.querySelector('#srt-qualify-form');

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var REP_EMAIL_DOMAINS = ['@restaurantware.com', '@rw.team'];
  function isAllowedRepEmail(email) {
    var e = String(email).toLowerCase();
    return REP_EMAIL_DOMAINS.some(function (d) { return e.slice(-d.length) === d; });
  }

  // Restore any prior draft. packingType resolves to a RadioNodeList, whose
  // .value setter checks the matching radio.
  (function () {
    var prior = sGet('sst_qualify', {});
    Object.keys(prior).forEach(function (k) {
      var el = qualifyForm.elements[k];
      if (el) el.value = prior[k];
    });
  })();

  qualifyForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var data = {};
    for (var i = 0; i < qualifyForm.elements.length; i++) {
      var el = qualifyForm.elements[i];
      if (el.type === 'radio' && !el.checked) continue;
      if (el.name) data[el.name] = el.value.trim();
    }
    clearFieldErrors(qualifyForm);
    var hasError = false;
    ['email', 'business', 'fullName', 'phone', 'repName', 'repEmail'].forEach(function (name) {
      if (!data[name]) {
        setFieldError(qualifyForm, name, 'This field is required.');
        hasError = true;
      }
    });
    // Rep email: format + company-domain check (mirrors the backend's
    // SALES_REP_ALLOWED_EMAIL_DOMAINS); no consumer-domain block or MX lookup.
    if (data.repEmail && !EMAIL_RE.test(data.repEmail)) {
      setFieldError(qualifyForm, 'repEmail', 'Please enter a valid email.');
      hasError = true;
    } else if (data.repEmail && !isAllowedRepEmail(data.repEmail)) {
      setFieldError(qualifyForm, 'repEmail', 'Please use your @restaurantware.com or @rw.team email.');
      hasError = true;
    }
    if (!data.packingType) {
      setGroupError(root.querySelector('#sst-packing-group'), 'Please choose a packing type.');
      hasError = true;
    }
    var domain = '';
    if (data.email) {
      if (!EMAIL_RE.test(data.email)) {
        setFieldError(qualifyForm, 'email', 'Please enter a valid email.');
        hasError = true;
      } else {
        domain = data.email.split('@')[1].toLowerCase();
        if (CONSUMER.indexOf(domain) !== -1) {
          setFieldError(qualifyForm, 'email', 'Please use your business email. Samples are for verified operators only.');
          hasError = true;
        } else if (DISPOSABLE.indexOf(domain) !== -1) {
          setFieldError(qualifyForm, 'email', 'Disposable email addresses are not accepted.');
          hasError = true;
        }
      }
    }
    if (data.phone) {
      var digits = data.phone.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 15) {
        setFieldError(qualifyForm, 'phone', 'Please enter a valid phone number.');
        hasError = true;
      }
    }
    if (hasError) { focusFirstError(qualifyForm); return; }

    // Domain check: DNS-over-HTTPS MX lookup with a fallback resolver.
    // Google answers first; if it is unreachable, Cloudflare is asked. Only
    // when BOTH providers fail do we allow through (fail open), so a single
    // outage never blocks a real operator.
    function lookupMx(dom) {
      function query(url, opts) {
        return fetch(url, opts).then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        }).then(function (j) {
          return !!(j.Answer && j.Answer.length);
        });
      }
      return query('https://dns.google/resolve?name=' + encodeURIComponent(dom) + '&type=MX', { cache: 'force-cache' })
        .catch(function () {
          return query('https://cloudflare-dns.com/dns-query?name=' + encodeURIComponent(dom) + '&type=MX', {
            cache: 'force-cache',
            headers: { accept: 'application/dns-json' }
          });
        });
    }

    var submitBtn = qualifyForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Verifying...';
    lookupMx(domain)
      .then(function (hasMx) {
        if (!hasMx) {
          setFieldError(qualifyForm, 'email', "We can't find a mail server for " + domain + '. Double-check the email address.');
          return false;
        }
        return true;
      })
      .catch(function () { return true; })
      .then(function (ok) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Continue →';
        if (!ok) return;
        sSet('sst_qualify', data);
        goToStep(2);
      });
  });

  /* ================================================================
     STEP 2 — PICK PRODUCTS
     ================================================================ */
  var gridEl = root.querySelector('#srt-grid');
  var searchEl = root.querySelector('#srt-search');
  var categoryEl = root.querySelector('#srt-category');
  var brandEl = root.querySelector('#srt-brand');
  var counterEl = root.querySelector('#srt-counter');
  var continueBtn = root.querySelector('#srt-continue-btn');
  var loadingEl = root.querySelector('#srt-loading');

  var allProducts = [];
  var productsRequested = false;
  var productsLoaded = false; // true once the full catalog has finished streaming in (or the load failed)
  var selected = {}; // { sku: qty }

  (function () {
    var prior = sGet('sst_selected', {});
    if (Array.isArray(prior)) {
      prior.forEach(function (sku) { selected[sku] = 1; });
    } else if (prior && typeof prior === 'object') {
      selected = prior;
    }
  })();

  function totalPieces() {
    return Object.keys(selected).reduce(function (n, k) { return n + selected[k]; }, 0);
  }

  function saveSel() { sSet('sst_selected', selected); }

  function isOverLimit() { return totalPieces() > APPROVAL_THRESHOLD; }
  function isApproved() { return !!sGet('sst_approved', false); }

  var counterLabelEl = root.querySelector('#srt-counter-label');

  /* No hard cap for Sales Reps: going over the standard limit only flags the
     counter. Dropping back to the limit or below discards any prior approval,
     so a later over-limit kit has to be confirmed again. */
  function updateCounter() {
    var tot = totalPieces();
    var over = tot > APPROVAL_THRESHOLD;
    counterEl.textContent = tot;
    counterEl.classList.toggle('srt-counter--max', over);
    if (counterLabelEl) counterLabelEl.textContent = ' pieces' + (over ? ' · approval required' : '');
    if (!over) sDel('sst_approved');
    continueBtn.disabled = tot === 0;
  }

  function changeQty(sku, delta) {
    var current = selected[sku] || 0;
    var next = current + delta;
    if (next < 0) return;
    if (next === 0) {
      delete selected[sku];
    } else {
      selected[sku] = next;
    }
    saveSel();
    renderGrid();
  }

  var IMG_PLACEHOLDER = '/cdn/shop/files/rw_placeholder.png';
  // Swaps a broken image for the placeholder; onerror is cleared first so a
  // missing placeholder can't loop.
  var IMG_FALLBACK = ' onerror="this.onerror=null;this.src=\'' + IMG_PLACEHOLDER + '\';"';

  function imgUrl(src, width) {
    if (!src) return IMG_PLACEHOLDER;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* Card titles link to the product page in a new tab so browsing details
     never navigates away from the in-progress request. */
  function titleHtml(p) {
    return p.handle
      ? '<a href="/products/' + escapeHtml(p.handle) + '" target="_blank" rel="noopener">' + escapeHtml(p.title) + '</a>'
      : escapeHtml(p.title);
  }

  function cardHtml(p) {
    var qty = selected[p.sku] || 0;
    var isSel = qty > 0;
    var packStr = p.packSize ? ' &middot; ' + p.packSize + ' ct' : '';
    if (!p.eligible) {
      return '<div class="srt-product-card srt-ineligible ' + (p.ineligibleReason === 'Out of stock' ? 'srt-oos' : '') + '" data-sku="' + escapeHtml(p.sku) + '">' +
        '<div class="srt-product-media" data-reason="' + escapeHtml(p.ineligibleReason || '') + '">' +
        '<img src="' + escapeHtml(imgUrl(p.image, 360)) + '" alt="" loading="lazy"' + IMG_FALLBACK + '>' +
        '</div>' +
        '<div class="srt-title">' + escapeHtml(p.title) + '</div>' +
        '<div class="srt-sku">' + escapeHtml(p.sku) + packStr + '</div>' +
        '<div class="srt-price">$' + p.price.toFixed(2) + '</div>' +
        '</div>';
    }
    return '<div class="srt-product-card ' + (isSel ? 'srt-selected' : '') + '" data-sku="' + escapeHtml(p.sku) + '">' +
      (isSel ? '<div class="srt-check">&#10003;</div>' : '') +
      '<img src="' + escapeHtml(imgUrl(p.image, 360)) + '" alt="" loading="lazy"' + IMG_FALLBACK + '>' +
      '<div class="srt-title">' + titleHtml(p) + '</div>' +
      '<div class="srt-sku">' + escapeHtml(p.sku) + packStr + '</div>' +
      '<div class="srt-price">$' + p.price.toFixed(2) + '</div>' +
      '<div class="srt-qty-controls">' +
      '<button type="button" class="srt-qty-btn" data-action="dec" data-sku="' + escapeHtml(p.sku) + '"' + (qty === 0 ? ' disabled' : '') + '>&minus;</button>' +
      '<span class="srt-qty-val">' + qty + '</span>' +
      '<button type="button" class="srt-qty-btn" data-action="inc" data-sku="' + escapeHtml(p.sku) + '">+</button>' +
      '</div></div>';
  }

  /* Search synonym expansion — a fallback layer for when Boost is unreachable
     (see boostSearchSkus below), so local search still understands that
     operators search in kitchen terms ("burgers", "to-go") that don't always
     appear verbatim in product titles. Groups are editable from the
     section's "Search synonyms" setting (one group per line, comma-
     separated); left blank, local search falls back to plain literal
     matching — Boost's own synonym/fuzzy matching is the primary path. */
  function parseSynonyms(raw) {
    return String(raw || '').split(/\r?\n/).map(function (line) {
      return line.split(',').map(function (phrase) { return phrase.trim().toLowerCase(); }).filter(Boolean);
    }).filter(function (group) { return group.length > 1; });
  }
  var SYNONYMS = parseSynonyms(filterData.searchSynonyms);
  var SYNONYM_MAP = {};
  SYNONYMS.forEach(function (group) {
    group.forEach(function (phrase) { SYNONYM_MAP[phrase] = group; });
  });
  // Longest phrases first so "hot cup" is matched before the plain "cup" inside it.
  var SYNONYM_PHRASES = Object.keys(SYNONYM_MAP).sort(function (a, b) { return b.length - a.length; });

  /* Expands a raw query into every phrase variant reachable by swapping one
     recognized synonym phrase for another. Always includes the original query. */
  function expandQuery(q) {
    var query = q.trim().toLowerCase();
    var variants = [query];
    if (!query) return variants;
    SYNONYM_PHRASES.forEach(function (phrase) {
      if (query.indexOf(phrase) === -1) return;
      SYNONYM_MAP[phrase].forEach(function (alt) {
        if (alt === phrase) return;
        variants.push(query.split(phrase).join(alt));
      });
    });
    return variants;
  }

  function matchProduct(p, query) {
    var q = query.trim().toLowerCase();
    // Boost's result set wins once it has answered for this exact query — it
    // covers fuzzy/typo/synonym matching Boost already runs site-wide. Until
    // then (or if Boost is unavailable), fall back to the local matcher below.
    if (q && boostQuery === q && boostSkuMap) {
      if (boostSkuMap[String(p.sku).toUpperCase()]) return true;
      // Retail products carry a `pfs:hidden:s` / `pfs:hidden` tag and are
      // excluded from Boost's main-search index by design, so they never appear in
      // boostSkuMap. They ARE loaded into the sample tool's catalog, though,
      // and the tool must surface them (e.g. RWP0005C's retail sibling
      // RWP0005C-20). A non-retail product Boost didn't return is genuinely
      // non-matching, so trust Boost and drop it; a retail product is absent
      // by tag rather than by relevance, so let the local matcher below judge
      // it instead of hiding it.
      if (p.tags.indexOf('pfs:hidden:s') === -1 && p.tags.indexOf('pfs:hidden') === -1) return false;
    }
    var hay = (p.sku + ' ' + p.title + ' ' + (p.category || '') + ' ' + (p.brand || '')).toLowerCase();
    var hayNoSpace = hay.replace(/\s+/g, '');
    return expandQuery(query).some(function (variant) {
      var tokens = variant.split(/\s+/).filter(Boolean);
      if (tokens.length === 0) return true;
      return tokens.every(function (t) {
        return hay.indexOf(t) !== -1 || hayNoSpace.indexOf(t.replace(/\s+/g, '')) !== -1;
      });
    });
  }

  /* RW collections are tag-automated: every product in a category collection
     shares one cat-* tag, every product in a brand collection shares one
     col-* tag. A 10-product sample of the chosen collection reveals that tag
     (majority vote), which then filters the already-loaded catalog — so a
     dropdown change costs one small request, once, per collection. */
  var resolvedTags = {};
  function resolveCollectionTag(handle, prefix) {
    if (!handle || handle in resolvedTags) {
      return Promise.resolve(handle ? resolvedTags[handle] : null);
    }
    return fetch('/collections/' + handle + '/products.json?limit=10')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var counts = {};
        (data.products || []).forEach(function (p) {
          normalizeTags(p.tags).forEach(function (t) {
            if (t.indexOf(prefix) === 0) counts[t] = (counts[t] || 0) + 1;
          });
        });
        var best = null;
        Object.keys(counts).forEach(function (t) {
          if (best === null || counts[t] > counts[best]) best = t;
        });
        resolvedTags[handle] = best;
        return best;
      })
      .catch(function () { return null; }); // transient failure — not cached, retried on next change
  }

  // Select values are collection handles; each resolves (once) to the tag its
  // products share. Unresolved handles don't filter, so the grid never blanks
  // out while the sample request is in flight.
  function matchesSelect(p, handle) {
    if (!handle) return true;
    var tag = resolvedTags[handle];
    if (!tag) return true;
    return p.tags.indexOf(tag) !== -1;
  }

  var PAGE_SIZE = 24;
  var gridPage = 1;

  function paginationHtml(totalPages, totalItems) {
    return '<div class="srt-pagination">' +
      '<button type="button" class="srt-page-btn" data-page="' + (gridPage - 1) + '"' + (gridPage <= 1 ? ' disabled' : '') + '>&larr; Prev</button>' +
      '<span class="srt-page-info">Page ' + gridPage + ' of ' + totalPages + ' &middot; ' + totalItems + ' products</span>' +
      '<button type="button" class="srt-page-btn" data-page="' + (gridPage + 1) + '"' + (gridPage >= totalPages ? ' disabled' : '') + '>Next &rarr;</button>' +
      '</div>';
  }

  function renderGrid() {
    if (!allProducts.length) { updateCounter(); return; }
    var cat = categoryEl.value;
    var brand = brandEl.value;
    var filtered = allProducts.filter(function (p) {
      if (!matchesSelect(p, cat, 'category')) return false;
      if (!matchesSelect(p, brand, 'brand')) return false;
      return matchProduct(p, searchEl.value);
    });
    // Eligible first, then out of stock, then not eligible — grayed cards stay
    // visible so operators can see what exists but can't sample
    function sortRank(p) {
      if (p.eligible) return 0;
      return p.ineligibleReason === 'Out of stock' ? 1 : 2;
    }
    filtered.sort(function (a, b) { return sortRank(a) - sortRank(b); });

    if (filtered.length === 0) {
      // Don't flash the harsh empty state prematurely: the match may still be
      // arriving. Boost could still be resolving typo/plural/synonym matches,
      // or the catalog itself may still be streaming in (a searched SKU can
      // live on a page that hasn't been fetched yet). Keep "Searching…" until
      // both have settled, then show the real "no match" message.
      var stillLoading = boostPending || !productsLoaded;
      var note = stillLoading ? 'Searching…' : 'No products match your filters.';
      gridEl.innerHTML = '<div class="srt-grid-note" style="padding:40px;">' + note + '</div>';
      updateCounter();
      return;
    }
    var totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (gridPage > totalPages) gridPage = totalPages;
    if (gridPage < 1) gridPage = 1;
    var start = (gridPage - 1) * PAGE_SIZE;
    gridEl.innerHTML = filtered.slice(start, start + PAGE_SIZE).map(cardHtml).join('');
    if (totalPages > 1) {
      gridEl.insertAdjacentHTML('beforeend', paginationHtml(totalPages, filtered.length));
    }
    updateCounter();
  }

  gridEl.addEventListener('click', function (e) {
    var btn = e.target.closest('.srt-qty-btn');
    if (btn) {
      e.stopPropagation();
      changeQty(btn.dataset.sku, btn.dataset.action === 'inc' ? 1 : -1);
      return;
    }
    var pageBtn = e.target.closest('.srt-page-btn');
    if (pageBtn && !pageBtn.disabled) {
      gridPage = parseInt(pageBtn.dataset.page, 10) || 1;
      renderGrid();
      var y = gridEl.getBoundingClientRect().top + window.scrollY - 160;
      window.scrollTo({ top: y < 0 ? 0 : y, behavior: 'smooth' });
    }
  });

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, ms);
    };
  }

  searchEl.addEventListener('input', debounce(function () {
    if (searchEl.value.trim()) {
      if (categoryEl.value) categoryEl.value = '';
      if (brandEl.value) brandEl.value = '';
    }
    gridPage = 1;
    var q = searchEl.value.trim().toLowerCase();
    boostPending = !!q;
    renderGrid(); // instant feedback from the local matcher while Boost is in flight
    if (q) {
      boostSearchSkus(q).then(function (skus) {
        if (searchEl.value.trim().toLowerCase() !== q) return; // stale response, user kept typing
        boostPending = false;
        boostQuery = q;
        boostSkuMap = skus;
        renderGrid();
      });
    }
  }, 120));
  categoryEl.addEventListener('change', function () {
    gridPage = 1;
    resolveCollectionTag(categoryEl.value, 'cat-').then(renderGrid);
  });
  brandEl.addEventListener('change', function () {
    gridPage = 1;
    resolveCollectionTag(brandEl.value, 'col-').then(renderGrid);
  });

  /* ---------- persistent catalog cache (IndexedDB) ----------
     The full catalog is ~19k products across ~76 paged requests and would
     otherwise be re-downloaded on every page load / refresh. It's cached in
     IndexedDB (too large for localStorage's ~5MB) so a refresh or repeat visit
     paints the picker instantly. Strategy is stale-while-revalidate: a cached
     catalog renders immediately, and only when it's older than the TTL do we
     refresh silently in the background — so the shopper never waits on the
     network, and availability / "out of stock" status is never more than the
     TTL stale. Everything here fails safe: if IndexedDB is unavailable, blocked
     (private mode), or throws, loadProducts() falls straight through to the
     original network-only path with no behaviour change.

     CACHE VERSION: bump the 'v1' in CATALOG_CACHE_KEY whenever flattenProducts'
     output shape or the eligibility rules / disposable_cats scope change, so
     entries written by the old logic are discarded. The collection handle is
     part of the key so switching the section's collection never serves the
     wrong catalog. */
  var CATALOG_CACHE_KEY = 'catalog_v1_' + COLLECTION;
  var CATALOG_CACHE_TTL = 60 * 60 * 1000; // 1 hour — bounds how stale availability can be
  var IDB_NAME = 'srt-cache';
  var IDB_STORE = 'kv';

  function idbOpen() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) { reject(new Error('no-idb')); return; }
      var req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
      };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
      req.onblocked = function () { reject(new Error('blocked')); };
    });
  }
  function idbGet(key) {
    return idbOpen().then(function (db) {
      return new Promise(function (resolve, reject) {
        var req = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
        req.onsuccess = function () { resolve(req.result || null); };
        req.onerror = function () { reject(req.error); };
      });
    });
  }
  function idbSet(key, val) {
    return idbOpen().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(val, key);
        tx.oncomplete = function () { resolve(true); };
        tx.onerror = function () { reject(tx.error); };
        tx.onabort = function () { reject(tx.error || new Error('abort')); };
      });
    });
  }

  /* Refreshes the step-4 recap once product data arrives, if the user is
     already on the confirmation step (reached via the post-submit redirect). */
  function refreshConfirmRecap() {
    if (root.querySelector('.srt-step[data-step="4"].srt-step--active')) {
      root.querySelector('#srt-confirm-summary').innerHTML = summaryHtml(kitRows());
    }
  }

  /* Live Shopify loader — paginated public collection endpoint, no API key.
     The full catalog is shown; products run through the dev-guide eligibility
     rules in flattenProducts: rule breakers show grayed as "Not eligible for
     samples", unavailable variants as "Out of stock". */
  function loadProducts() {
    if (productsRequested) return;
    productsRequested = true;
    loadingEl.style.display = '';

    // Cache-first: a stored catalog paints instantly on refresh/repeat visits.
    // A fresh entry skips the network entirely; a stale one still paints
    // instantly, then revalidates silently. A miss — or any IndexedDB error —
    // runs the full visible network load.
    idbGet(CATALOG_CACHE_KEY)
      .then(function (cached) {
        if (cached && cached.products && cached.products.length) {
          allProducts = cached.products;
          productsLoaded = true; // a full catalog is on screen; search can show real empty states
          loadingEl.style.display = 'none';
          renderGrid();
          refreshConfirmRecap();
          var isFresh = cached.ts && (Date.now() - cached.ts) < CATALOG_CACHE_TTL;
          if (!isFresh) networkLoad(true); // stale — refresh in the background
        } else {
          networkLoad(false); // nothing cached — normal visible load
        }
      })
      .catch(function () { networkLoad(false); }); // IndexedDB unavailable — behave exactly as before
  }

  /* Full network load of the catalog. `silent` = true is a background
     revalidation behind an already-painted cached grid: the loading indicator
     stays hidden and the grid is swapped only once, on completion, so the user
     is never disrupted mid-browse. On a cold load (`silent` = false) each wave
     renders as it arrives. On success the complete catalog is persisted to
     IndexedDB so the next load/refresh reads it instead of the network. */
  function networkLoad(silent) {
    if (!silent) loadingEl.style.display = '';
    var all = [];
    // Each fetched page is flattened and rendered immediately, so the first
    // 250 products are on screen while the rest of the catalog streams in.
    function applyBatch(done) {
      // During a silent revalidation, hold off re-rendering until the full set
      // is in — the cached grid stays put and is replaced in one clean swap.
      if (silent && !done) return;
      allProducts = flattenProducts(all);
      // Once the last page is in, mark the catalog loaded so a still-empty
      // search can switch from "Searching…" to the real "no match" message.
      if (done) productsLoaded = true;
      // The indicator is only useful while the grid is still empty; once the
      // first batch is on screen the remaining pages stream in silently.
      if (!silent) loadingEl.style.display = done || allProducts.length ? 'none' : '';
      renderGrid();
      refreshConfirmRecap();
    }
    function fetchPage(page) {
      return fetch('/collections/' + COLLECTION + '/products.json?limit=250&page=' + page)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) { return data.products || []; });
    }
    // Pages load in concurrent waves so a large catalog arrives several times
    // faster than one request at a time. Results are appended in page order to
    // keep the collection's sort stable; a short page marks the end (Shopify
    // returns an empty list past the last page, so overshoot is harmless).
    var WAVE = 4;
    function fetchWave(startPage) {
      var requests = [];
      for (var i = 0; i < WAVE; i++) requests.push(fetchPage(startPage + i));
      return Promise.all(requests).then(function (pages) {
        var done = false;
        pages.forEach(function (products) {
          if (done) return;
          all = all.concat(products);
          if (products.length < 250) done = true;
        });
        applyBatch(done);
        if (!done) return fetchWave(startPage + WAVE);
      });
    }
    fetchWave(1)
      .then(function () {
        // Persist the complete catalog for the next load/refresh. Best-effort:
        // a quota/write error just means the next load falls back to network.
        idbSet(CATALOG_CACHE_KEY, { ts: Date.now(), products: allProducts }).catch(function () {});
      })
      .catch(function () {
        // A total load failure leaves the grid empty; hide the indicator so
        // "Loading products..." doesn't sit on screen forever, and mark the
        // load settled so a no-match search doesn't stay stuck on "Searching…".
        // A failed silent revalidation just leaves the cached grid untouched.
        if (!silent) {
          productsLoaded = true;
          loadingEl.style.display = 'none';
          renderGrid();
        }
      });
  }

  function normalizeTags(tags) {
    if (typeof tags === 'string') tags = tags.split(',');
    if (!Array.isArray(tags)) return [];
    return tags.map(function (t) { return String(t).trim().toLowerCase(); });
  }

  function flattenProducts(products) {
    var result = [];
    products.forEach(function (p) {
      var tags = normalizeTags(p.tags);
      // Disposable-menu scope: out-of-scope products show grayed, not hidden
      var inScope = !DISPOSABLE_CATS.length || tags.some(function (t) { return DISPOSABLE_CATS.indexOf(t) !== -1; });
      // Hard exclusions — never shown in the tool
      if (/^custom\s+/i.test(p.title)) return;
      if (/\bkid(s|'s)?\b/i.test(p.title)) return;
      if (/\bsample\b/i.test(p.title)) return;
      if (tags.indexOf('hide_sample_tool') !== -1) return;
      var image = (p.images && p.images[0] && p.images[0].src) || '';
      var packMatch = p.title.match(/(\d[\d,]*)\s*(?:count|ct|pack|pk|piece|pcs)\b/i);
      var packSize = packMatch ? parseInt(packMatch[1].replace(/,/g, ''), 10) : null;
      (p.variants || []).forEach(function (v) {
        var sku = (v.sku || '').trim();
        if (!sku) return;
        if (/^(CRW|CUSTOM|BNR)/i.test(sku)) return;
        var ineligibleReason = null;
        if (!inScope) ineligibleReason = 'Not eligible for samples'; // outside the disposable menu
        else if (/-MC$/i.test(sku)) ineligibleReason = 'Not eligible for samples'; // master case duplicate
        else if (packSize !== null && packSize < 20) ineligibleReason = 'Not eligible for samples';
        else if (!DISPOSABLE_ADJ.test(p.title) && (REUSABLE_TITLE.test(p.title) || JANITORIAL_TITLE.test(p.title))) ineligibleReason = 'Not eligible for samples';
        else if (v.available === false) ineligibleReason = 'Out of stock';
        result.push({
          sku: sku,
          title: v.title && v.title !== 'Default Title' ? p.title + ' - ' + v.title : p.title,
          handle: p.handle || '',
          image: image,
          price: parseFloat(v.price) || 0,
          category: p.product_type || 'Other',
          brand: p.vendor === 'Restaurantware' ? null : p.vendor,
          tags: tags,
          packSize: packSize,
          eligible: ineligibleReason === null,
          ineligibleReason: ineligibleReason
        });
      });
    });
    return result;
  }

  // Dropdowns are filled once at init from the section-rendered collection
  // lists — available immediately, no waiting on the catalog fetch. Option
  // values are collection handles (resolved to tags on first selection).
  /* The native select popup sizes itself to the widest option, so very long
     collection titles make it open far wider than the field and overlap the
     neighboring controls. Labels are capped; the full title stays available
     as a hover tooltip. */
  var MAX_OPTION_LABEL = 32;
  function fillCollectionSelect(sel, items) {
    (items || []).forEach(function (entry) {
      if (!entry || !entry.handle || !entry.title) return;
      var opt = document.createElement('option');
      opt.value = entry.handle;
      var title = String(entry.title);
      if (title.length > MAX_OPTION_LABEL) {
        opt.textContent = title.slice(0, MAX_OPTION_LABEL).replace(/\s+\S*$/, '') + '…';
        opt.title = title;
      } else {
        opt.textContent = title;
      }
      sel.appendChild(opt);
    });
  }
  /* Dropdown data comes from the srt-filter-data section (Section Rendering
     API): every collection with restaurantware.indexable = true, split into
     categories vs collections by restaurantware.is_collection. The store has
     hundreds of collections and Liquid renders at most 50 per request, so the
     pages are fetched once, merged, and cached in localStorage. */
  var FILTER_CACHE_KEY = 'srt_filter_cache_v9';
  var FILTER_CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

  function fetchFilterPage(page) {
    return fetch('/?section_id=srt-filter-data&page=' + page)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        // Section rendering wraps the JSON in a <div id="shopify-section-...">
        return JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1));
      });
  }

  /* Liveness is enforced server-side: the srt-filter-data section skips any
     collection whose published_at is blank, so only collections live on the
     store reach these dropdowns. */
  function loadFilterOptions() {
    var cached = sGet(FILTER_CACHE_KEY, null);
    if (cached && cached.ts && Date.now() - cached.ts < FILTER_CACHE_TTL) {
      fillCollectionSelect(categoryEl, cached.categories);
      fillCollectionSelect(brandEl, cached.collections);
      return;
    }
    fetchFilterPage(1)
      .then(function (first) {
        var rest = [];
        for (var p = 2; p <= (first.pages || 1); p++) rest.push(fetchFilterPage(p));
        return Promise.all([first].concat(rest));
      })
      .then(function (pages) {
        var data = { ts: Date.now(), categories: [], collections: [] };
        pages.forEach(function (pg) {
          data.categories = data.categories.concat(pg.categories || []);
          data.collections = data.collections.concat(pg.collections || []);
        });
        sSet(FILTER_CACHE_KEY, data);
        fillCollectionSelect(categoryEl, data.categories);
        fillCollectionSelect(brandEl, data.collections);
      })
      .catch(function () { /* dropdowns stay on "All ..."; retried next visit */ });
  }
  loadFilterOptions();

  /* ---------- over-limit approval popup ----------
     A kit above APPROVAL_THRESHOLD pieces needs the Rep to confirm it was
     approved before moving on to shipping. The confirmation is stored in
     sst_approved (cleared by updateCounter once the kit is back within the
     limit). The checkbox resets every time the popup opens. */
  var approvalModal = root.querySelector('#sst-approval-modal');
  var approvalCheck = root.querySelector('#sst-approval-check');
  var approvalConfirm = root.querySelector('#sst-approval-confirm');
  var approvalCancel = root.querySelector('#sst-approval-cancel');
  var approvalReturnFocus = null;

  function openApprovalModal() {
    approvalReturnFocus = document.activeElement;
    approvalCheck.checked = false;
    approvalConfirm.disabled = true;
    approvalModal.hidden = false;
    approvalCheck.focus();
  }
  function closeApprovalModal() {
    approvalModal.hidden = true;
    if (approvalReturnFocus && approvalReturnFocus.focus) approvalReturnFocus.focus();
    approvalReturnFocus = null;
  }

  approvalCheck.addEventListener('change', function () {
    approvalConfirm.disabled = !approvalCheck.checked;
  });
  approvalCancel.addEventListener('click', closeApprovalModal);
  approvalConfirm.addEventListener('click', function () {
    if (!approvalCheck.checked) return;
    sSet('sst_approved', true);
    closeApprovalModal();
    goToStep(3);
  });
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !approvalModal.hidden) closeApprovalModal();
  });

  continueBtn.addEventListener('click', function () {
    if (totalPieces() === 0) return;
    if (isOverLimit() && !isApproved()) { openApprovalModal(); return; }
    goToStep(3);
  });

  root.querySelector('#srt-back-to-1').addEventListener('click', function () { goToStep(1); });

  /* ================================================================
     STEP 3 — REVIEW & SHIP
     ================================================================ */
  /* Contiguous US + DC only. AK, HI, and territories intentionally excluded.
     Deliberately hardcoded: Shopify's all_country_option_tags province data
     (used by the register pages) carries full state names only — no 2-letter
     codes — and the codes here are load-bearing for the Google-autocomplete
     fill (short_name), the ZIP-to-state match, and the ERP payload contract
     ("state": "CA"). The state list itself is static civic data. */
  var STATES = [
    ['AL', 'Alabama'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'],
    ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'],
    ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'],
    ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'],
    ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
    ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
    ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
    ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
    ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
    ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming']
  ];

  var stateEl = root.querySelector('#srt-ship-state');
  STATES.forEach(function (pair) {
    var o = document.createElement('option');
    o.value = pair[0];
    o.textContent = pair[0] + ' — ' + pair[1];
    stateEl.appendChild(o);
  });

  var zipEl = root.querySelector('#srt-ship-zip');
  var cityEl = root.querySelector('#srt-ship-city');
  var zipStatusEl = root.querySelector('#srt-zip-status');
  var lastZip = '';
  // The last ZIP lookup that resolved in-region — used at submit time to
  // catch a City/State the customer typed or edited afterward that no
  // longer matches the ZIP (e.g. autofilled "Beverly Hills, CA" left in
  // place while City/State are hand-edited to "Wilmington, DE").
  var zipVerified = null;

  function verifyZip() {
    var zip = zipEl.value.trim().slice(0, 5);
    if (zip.length !== 5 || !/^\d{5}$/.test(zip) || zip === lastZip) return;
    lastZip = zip;
    zipVerified = null;
    zipStatusEl.textContent = 'Verifying...';
    zipStatusEl.className = 'srt-zip-status srt-zip-status--muted';
    fetch('https://api.zippopotam.us/us/' + zip)
      .then(function (r) {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(function (j) {
        var place = j.places && j.places[0];
        if (!place) throw new Error('no place');
        var state = place['state abbreviation'];
        var city = place['place name'];
        if (['AK', 'HI', 'PR', 'GU', 'VI', 'AS', 'MP'].indexOf(state) !== -1) {
          zipStatusEl.textContent = 'We only ship to the contiguous US.';
          zipStatusEl.className = 'srt-zip-status srt-zip-status--err';
          return;
        }
        if (!cityEl.value) cityEl.value = city;
        stateEl.value = state;
        clearFieldError(cityEl);
        clearFieldError(stateEl);
        zipVerified = { zip: zip, city: city, state: state };
        zipStatusEl.textContent = '✓ ' + city + ', ' + state;
        zipStatusEl.className = 'srt-zip-status srt-zip-status--ok';
      })
      .catch(function () {
        zipStatusEl.textContent = 'ZIP not found.';
        zipStatusEl.className = 'srt-zip-status srt-zip-status--err';
      });
  }
  zipEl.addEventListener('blur', verifyZip);
  zipEl.addEventListener('input', function () {
    if (zipEl.value.length === 5) {
      verifyZip();
    } else {
      // Field no longer holds a checked ZIP — drop the stale status message.
      lastZip = '';
      zipVerified = null;
      zipStatusEl.textContent = '';
      zipStatusEl.className = 'srt-zip-status';
    }
  });

  function normalizeCity(s) {
    return String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }
  function citiesMatch(a, b) {
    a = normalizeCity(a);
    b = normalizeCity(b);
    return a === b || a.indexOf(b) !== -1 || b.indexOf(a) !== -1;
  }
  /* Reconciles the typed City/State against the last ZIP lookup that
     actually matched the ZIP currently in the field. Returns true (passes)
     when there is nothing to compare — e.g. the lookup never resolved — so
     a flaky/offline ZIP API fails open instead of blocking checkout. */
  function checkAddressMatch(form) {
    if (!zipVerified) return true;
    var zip = zipEl.value.trim().slice(0, 5);
    if (zip !== zipVerified.zip) return true;
    var ok = true;
    if (stateEl.value && stateEl.value !== zipVerified.state) {
      setFieldError(form, 'ship-state', 'ZIP ' + zip + ' is in ' + zipVerified.state + ', not ' + stateEl.value + '.');
      ok = false;
    }
    if (cityEl.value && !citiesMatch(cityEl.value, zipVerified.city)) {
      setFieldError(form, 'ship-city', 'ZIP ' + zip + ' matches ' + zipVerified.city + ', not "' + cityEl.value + '".');
      ok = false;
    }
    return ok;
  }

  /* Google Places autocomplete on the street address (step 3). Called by the
     Maps JS API loader injected below (callback=initAutocomplete). Picking a
     suggestion fills street, city, state, and ZIP, then re-runs the ZIP check
     so the contiguous-US rule still applies. */
  var addressEl = root.querySelector('#srt-ship-address');
  window.initAutocomplete = function () {
    if (!addressEl || !window.google || !google.maps || !google.maps.places) return;
    var autocomplete = new google.maps.places.Autocomplete(addressEl, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['address_components']
    });
    // Enter while a suggestion is highlighted must pick it, not submit the form
    addressEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });
    autocomplete.addListener('place_changed', function () {
      var place = autocomplete.getPlace();
      if (!place || !place.address_components) return;
      var comp = {};
      place.address_components.forEach(function (c) {
        c.types.forEach(function (t) { comp[t] = c; });
      });
      var street = ((comp.street_number ? comp.street_number.long_name : '') + ' ' +
        (comp.route ? comp.route.long_name : '')).trim();
      if (street) { addressEl.value = street; clearFieldError(addressEl); }
      var city = comp.locality || comp.sublocality_level_1 || comp.postal_town;
      if (city) { cityEl.value = city.long_name; clearFieldError(cityEl); }
      if (comp.administrative_area_level_1) {
        stateEl.value = comp.administrative_area_level_1.short_name;
        clearFieldError(stateEl);
      }
      if (comp.postal_code) {
        zipEl.value = comp.postal_code.long_name;
        clearFieldError(zipEl);
        lastZip = '';
        verifyZip();
      }
    });
  };

  if (MAPS_API_KEY && !window.google) {
    var mapsScript = document.createElement('script');
    mapsScript.src = 'https://maps.googleapis.com/maps/api/js?key=' +
      encodeURIComponent(MAPS_API_KEY) + '&libraries=places&callback=initAutocomplete';
    mapsScript.defer = true;
    document.head.appendChild(mapsScript);
  }

  function kitRows() {
    var bySku = {};
    allProducts.forEach(function (p) { bySku[p.sku] = p; });
    return Object.keys(selected).map(function (sku) {
      var p = bySku[sku] || { sku: sku, title: sku, image: '', handle: '' };
      return {
        sku: sku,
        title: p.title,
        image: p.image,
        url: p.handle ? '/products/' + p.handle : '',
        qty: selected[sku] || 1
      };
    });
  }

  /* `removable` adds an × button per row (shipping step only); the recap on
     the confirmation step stays read-only. Titles link to the product page in
     a new tab so the in-progress request is not navigated away from. */
  function summaryHtml(rows, removable) {
    return rows.map(function (r) {
      var title = r.url
        ? '<a href="' + escapeHtml(r.url) + '" target="_blank" rel="noopener">' + escapeHtml(r.title) + '</a>'
        : escapeHtml(r.title);
      return '<div class="srt-summary-row">' +
        '<img src="' + escapeHtml(imgUrl(r.image, 96)) + '" alt=""' + IMG_FALLBACK + '>' +
        '<div class="srt-info">' +
        '<div class="srt-t">' + title + '</div>' +
        '<div class="srt-s">' + r.qty + ' piece' + (r.qty === 1 ? '' : 's') + '</div>' +
        '</div>' +
        (removable
          ? '<button type="button" class="srt-summary-remove" data-remove-sku="' + escapeHtml(r.sku) + '" aria-label="Remove ' + escapeHtml(r.title) + '">&times;</button>'
          : '') +
        '</div>';
    }).join('');
  }

  /* "N products · X pieces" line above the kit summary (shipping step), with
     " · approved over-limit" appended when the kit exceeds the standard limit. */
  function kitRecapHtml(rows) {
    return rows.length + ' product' + (rows.length === 1 ? '' : 's') + ' &middot; ' + totalPieces() + ' pieces' +
      (isOverLimit() && isApproved() ? ' &middot; approved over-limit' : '');
  }

  function enterShipping() {
    var qualify = sGet('sst_qualify', null);
    if (!qualify) { goToStep(1); return; }
    if (totalPieces() === 0) { goToStep(2); return; }
    // Refresh/back paths can land here without the over-limit confirmation.
    if (isOverLimit() && !isApproved()) { goToStep(2); return; }
    var rows = kitRows();
    root.querySelector('#srt-kit-recap').innerHTML = kitRecapHtml(rows);
    root.querySelector('#srt-summary').innerHTML = summaryHtml(rows, true);
    var firstEl = root.querySelector('#srt-ship-first-name');
    var lastEl = root.querySelector('#srt-ship-last-name');
    if (!firstEl.value && !lastEl.value) {
      var nameParts = (qualify.fullName || '').trim().split(/\s+/);
      firstEl.value = nameParts.shift() || '';
      lastEl.value = nameParts.join(' ');
    }
    // Restore prior shipping draft
    var prior = sGet('sst_shipping', {});
    var shipForm = root.querySelector('#srt-ship-form');
    Object.keys(prior).forEach(function (k) {
      var el = shipForm.elements[k];
      if (el && !el.value) el.value = prior[k];
    });
    // Clear any ZIP status left over from a prior visit to this step (e.g.
    // going back to step 2 and forward again) so nothing shows until the
    // user actually types/edits the ZIP on this visit.
    lastZip = '';
    zipVerified = null;
    zipStatusEl.textContent = '';
    zipStatusEl.className = 'srt-zip-status';
  }

  root.querySelector('#srt-back-to-2').addEventListener('click', function () { goToStep(2); });

  /* Removing a line item on the shipping step; removing the last one sends
     the user back to step 2 since there is nothing left to ship. */
  root.querySelector('#srt-summary').addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-remove-sku]') : null;
    if (!btn) return;
    delete selected[btn.getAttribute('data-remove-sku')];
    saveSel();
    updateCounter();
    if (totalPieces() === 0) { goToStep(2); return; }
    var rows = kitRows();
    root.querySelector('#srt-kit-recap').innerHTML = kitRecapHtml(rows);
    root.querySelector('#srt-summary').innerHTML = summaryHtml(rows, true);
  });

  var shipForm = root.querySelector('#srt-ship-form');
  shipForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var data = {};
    for (var i = 0; i < shipForm.elements.length; i++) {
      var el = shipForm.elements[i];
      if (el.name) data[el.name] = el.value.trim();
    }
    clearFieldErrors(shipForm);
    var hasError = false;
    ['ship-first-name', 'ship-last-name', 'ship-address', 'ship-city', 'ship-state', 'ship-zip'].forEach(function (k) {
      if (!data[k]) {
        setFieldError(shipForm, k, 'This field is required.');
        hasError = true;
      }
    });
    if (data['ship-zip'] && !/^\d{5}(-\d{4})?$/.test(data['ship-zip'])) {
      setFieldError(shipForm, 'ship-zip', 'Enter a valid US ZIP.');
      hasError = true;
    }
    if (hasError) { focusFirstError(shipForm); return; }
    if (!checkAddressMatch(shipForm)) { focusFirstError(shipForm); return; }
    // Re-check the over-limit approval before anything is sent.
    if (isOverLimit() && !isApproved()) { goToStep(2); openApprovalModal(); return; }

    sSet('sst_shipping', data);
    var orderNum = 'SSR-' + Math.floor(100000 + Math.random() * 900000);
    sSet('sst_order', orderNum);

    var btn = root.querySelector('#srt-submit-btn');
    btn.disabled = true;
    btn.textContent = 'Processing...';
    if (EMAIL_ENDPOINT) {
      submitToEmailService(orderNum, data, btn);
    } else {
      submitContactForm(orderNum, data);
    }
  });

  /* Email-friendly timestamp in US Eastern time, matching the backend
     contract — e.g. "Fri, Jul 3 2026 at 8:24 AM EDT". */
  function formatSubmissionTimestamp() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short'
      }).formatToParts(new Date());
      var get = function (type) {
        for (var i = 0; i < parts.length; i++) {
          if (parts[i].type === type) return parts[i].value;
        }
        return '';
      };
      return get('weekday') + ', ' + get('month') + ' ' + get('day') + ' ' + get('year') +
        ' at ' + get('hour') + ':' + get('minute') + ' ' + get('dayPeriod') + ' ' + get('timeZoneName');
    } catch (e) {
      return new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' });
    }
  }

  /* POST the request as JSON to the backend email service configured in the
     section settings (e.g. .../api/v1/main-us/sample-kit/request). The service
     sends both the customer confirmation and the internal notification emails. */
  function submitToEmailService(orderNum, shipping, btn) {
    var qualify = sGet('sst_qualify', {});
    var rows = kitRows();
    var fullName = qualify.fullName || '';
    var payload = {
      fullName: fullName,
      customerFirstName: fullName.split(/\s+/)[0] || '',
      businessName: qualify.business || '',
      email: qualify.email || '',
      phone: qualify.phone || '',
      orderNumber: orderNum,
      submissionTimestamp: formatSubmissionTimestamp(),
      totalPieces: totalPieces(),
      totalSkus: rows.length,
      shippingAddress: {
        recipientFirstName: shipping['ship-first-name'],
        recipientLastName: shipping['ship-last-name'],
        recipientName: (shipping['ship-first-name'] + ' ' + shipping['ship-last-name']).trim(),
        businessName: qualify.business || '',
        street1: shipping['ship-address'],
        street2: '',
        city: shipping['ship-city'],
        state: shipping['ship-state'],
        zip: shipping['ship-zip']
      },
      items: rows.map(function (r) {
        return { productTitle: r.title, sku: r.sku, pieceCount: r.qty, imageUrl: r.image || '' };
      }),
      // Sales Rep additions on top of the customer tool's payload.
      repName: qualify.repName || '',
      repEmail: qualify.repEmail || '',
      packingType: qualify.packingType || '',
      overLimitApproved: isOverLimit() && isApproved(),
      salesManagerEmail: SALES_MANAGER_EMAIL,
      // Backend flag: routes the confirmation to repEmail instead of the
      // customer, and adds salesManagerEmail to the internal email over the limit.
      salesRepOrder: true
    };
    fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        goToStep(4);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Place sample order →';
        alert('We could not submit your request right now. Please try again in a moment.');
      });
  }

  /* Fill and post Shopify's native contact form. Shopify emails every
     contact[...] field to the store's sender email, then redirects back
     to this page with the posted flag — the init code below then shows step 4. */
  function submitContactForm(orderNum, shipping) {
    var contactForm = document.getElementById('srt-contact-form');
    if (!contactForm) { alert('Submission form is unavailable. Please refresh and try again.'); return; }
    var qualify = sGet('sst_qualify', {});
    var rows = kitRows();
    var totals = totalPieces();
    var overLimitApproved = isOverLimit() && isApproved();

    var lines = [];
    lines.push('SAMPLE REQUEST — ' + (qualify.business || '') + ' — ' + orderNum);
    lines.push('Submitted: ' + new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' }));
    lines.push('');
    lines.push('== OPERATOR ==');
    lines.push('Business: ' + (qualify.business || ''));
    lines.push('Contact: ' + (qualify.fullName || ''));
    lines.push('Email: ' + (qualify.email || ''));
    lines.push('Phone: ' + (qualify.phone || ''));
    lines.push('Rep Name: ' + (qualify.repName || ''));
    lines.push('Rep Email: ' + (qualify.repEmail || ''));
    lines.push('Packing Type: ' + (qualify.packingType || ''));
    lines.push('Over-limit approved: ' + (overLimitApproved ? 'Yes' : 'No'));
    lines.push('Sales Manager: ' + SALES_MANAGER_EMAIL);
    lines.push('');
    lines.push('== SHIP TO ==');
    lines.push('Recipient: ' + (shipping['ship-first-name'] + ' ' + shipping['ship-last-name']).trim());
    lines.push('Address: ' + shipping['ship-address']);
    lines.push('City / State / ZIP: ' + shipping['ship-city'] + ', ' + shipping['ship-state'] + ' ' + shipping['ship-zip']);
    lines.push('');
    lines.push('== KIT CONTENTS ==');
    rows.forEach(function (r) {
      lines.push(r.sku + ' | ' + r.title + ' | ' + r.qty + ' piece' + (r.qty === 1 ? '' : 's'));
    });
    lines.push('');
    lines.push('Total: ' + totals + ' pieces across ' + rows.length + ' SKUs.');

    contactForm.querySelector('[name="contact[email]"]').value = qualify.email || '';
    contactForm.querySelector('[name="contact[Name]"]').value = qualify.fullName || '';
    contactForm.querySelector('[name="contact[Business]"]').value = qualify.business || '';
    contactForm.querySelector('[name="contact[Phone]"]').value = qualify.phone || '';
    contactForm.querySelector('[name="contact[Order Number]"]').value = orderNum;
    contactForm.querySelector('[name="contact[Rep Name]"]').value = qualify.repName || '';
    contactForm.querySelector('[name="contact[Rep Email]"]').value = qualify.repEmail || '';
    contactForm.querySelector('[name="contact[Packing Type]"]').value = qualify.packingType || '';
    contactForm.querySelector('[name="contact[body]"]').value = lines.join('\n');
    contactForm.submit();
  }

  /* ================================================================
     STEP 4 — CONFIRMATION
     ================================================================ */
  function renderConfirmation() {
    // Tolerate missing/legacy state — show a partial confirmation rather than
    // bouncing the user, which reads as a broken flow.
    var qualify = sGet('sst_qualify', {}) || {};
    var orderNum = sGet('sst_order', null) || 'SSR-' + Math.floor(100000 + Math.random() * 900000);
    root.querySelector('#srt-order-num').textContent = orderNum;
    root.querySelector('#srt-email-echo').textContent = qualify.repEmail || 'your email';
    root.querySelector('#srt-packing-echo').textContent = qualify.packingType || '—';
    root.querySelector('#srt-confirm-summary').innerHTML = summaryHtml(kitRows());
    // The catalog prefetch kicked off at init refreshes this recap with
    // titles and images once products arrive (see applyBatch in loadProducts).
  }

  root.querySelector('#srt-start-over').addEventListener('click', function () {
    sDel('sst_qualify');
    sDel('sst_selected');
    sDel('sst_shipping');
    sDel('sst_order');
    sDel('sst_approved');
    selected = {};
    // Drop the contact_posted params so a refresh doesn't re-show step 4
    history.replaceState(null, '', window.location.pathname);
    var shipFormEl = root.querySelector('#srt-ship-form');
    shipFormEl.reset();
    qualifyForm.reset();
    var btn = root.querySelector('#srt-submit-btn');
    btn.disabled = false;
    btn.textContent = 'Place sample order →';
    goToStep(1);
  });

  /* ---------- init ---------- */
  // `posted` is computed up top, before the draft-state cleanup.
  goToStep(posted ? 4 : 1);
  // Prefetch the catalog right away — it downloads while the user fills in
  // step 1, so the picker is already populated when they reach step 2.
  loadProducts();
})();
